/*
 * SPDX-FileCopyrightText: 2023 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeliveryMode } from '@spartacus/cart/base/root';
import { BaseSiteService, CmsOrderDetailOverviewComponent, PaymentDetails, TranslationService, FeatureLevelDirective, CxDatePipe } from '@spartacus/core';
import { OrderDetailsService, OrderOverviewComponent } from '@spartacus/order/components';
import { Card, CmsComponentData, SpinnerComponent, CardComponent } from '@spartacus/storefront';
import { Observable, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { storeFrontConfig, USJB2BPortalConfig } from 'src/app/udxb2b/shared/constant/storefront-config';
import { AsyncPipe } from '@angular/common';
import { DayjsFormatPipe } from '../../../../utilities/date/dayjs-format.pipe';

@Component({
    selector: 'udx-cx-order-overview',
    templateUrl: './udx-order-overview.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent, CardComponent, FeatureLevelDirective, AsyncPipe, CxDatePipe, DayjsFormatPipe]
})
export class UdxOrderOverviewComponent extends OrderOverviewComponent implements OnInit {
  baseSiteService = inject(BaseSiteService);
  refOrdEnabled = false;
  dateFormat: string = 'yyyy/MM/dd';
  baseSite: string = '';
  usjfeature = false;
  isOrderConfirmation = false;
  isOrderDetails = false;
  constructor(
    translation: TranslationService,
    orderDetailsService: OrderDetailsService,
    component: CmsComponentData<CmsOrderDetailOverviewComponent>,
    private route: ActivatedRoute,
    private router: Router
  ) {
    super(translation, orderDetailsService, component);
  }
  ngOnInit(): void {
    this.baseSiteService.getActive()
    .pipe(take(1))
    .subscribe((baseSite) => {
    this.baseSite = baseSite;
      switch (baseSite) {
        case 'b2bportal':
          this.dateFormat = 'yyyy/MM/dd';
          break;

        default:
          this.dateFormat = 'MM/dd/yyyy';
          break;
      }
      storeFrontConfig
        .filter((config) => config.baseSiteId === baseSite)
        .map((config) => {
          this.refOrdEnabled = config.active;
        });

      USJB2BPortalConfig.filter((config) => config.baseSiteId === baseSite).map((config) => {
        this.usjfeature = config.active;
      });
        const path = this.route.routeConfig?.path;
    if (path?.includes('order-confirmation')) {
      this.isOrderConfirmation = true;
    } else if (path?.includes('orders/:orderCode')) {
      this.isOrderDetails = true;
    }
    });
  }

  override getDeliveryModeCardContent(deliveryMode: DeliveryMode): Observable<Card> {
    return this.translation.translate('orderDetails.deliveryMethod').pipe(
      filter(() => Boolean(deliveryMode)),
      map(
        (textTitle) =>
          ({
            title: textTitle,
            textBold: deliveryMode.name,
          } as Card)
      )
    );
  }

  getPaymentTypeCardContent(paymentType: any): Observable<Card> {
    return this.translation.translate('orderDetails.paymentType').pipe(
      filter(() => Boolean(paymentType)),
      map(
        (textTitle) =>
          ({
            title: textTitle,
            textBold: paymentType.displayName,
          } as Card)
      )
    );
  }

  getOrderCommentContent(comment: any): Observable<Card> {
    return this.translation.translate('orderDetails.rescheduleOrderComment').pipe(
      filter(() => Boolean(comment)),
      map(
        (textTitle) =>
          ({
            title: textTitle,
            textBold: comment,
          } as Card)
      )
    );
  }

  getRefOrder(refOrder: string): Observable<Card> {
    return this.translation.translate('orderDetails.refOrderID').pipe(
      filter(() => Boolean(refOrder)),
      map(
        (textTitle) =>
          ({
            title: textTitle,
            textBold: refOrder,
          } as Card)
      )
    );
  }

  getPaymentInfoCardContentUHU(payment: PaymentDetails): Observable<Card> {
    return combineLatest([
      this.translation.translate('paymentForm.payment'),
      this.translation.translate('paymentCard.expires', {
        month: Boolean(payment) ? payment.expiryMonth : '',
        year: Boolean(payment) ? payment.expiryYear : '',
      }),
    ]).pipe(
      filter(() => Boolean(payment)),
      map(([textTitle, textExpires]) => paymentMethodCardUHU(textTitle, payment))
  );
  }
  override getOrderCodeCardContent(orderCode: string): Observable<Card> {
    return combineLatest([
      this.translation.translate('orderDetails.orderNumber'),
      this.orderDetailsService.getOrderDetails(),
    ]).pipe(
      filter(([titleText, order]) => !!orderCode && !!order),
      map(([titleText, order]) => {
        const card: Card = {
          title: titleText,
          // text: [orderCode],
          actions: [],
        };

        if (this.isOrderConfirmation && orderCode) {
          card.actions?.push({
            link: this.router.createUrlTree(['/my-account/order', orderCode]).toString(),
            name: orderCode,
          });
        } else {
          // For normal order details page
          card.text = [orderCode];
        }

        return card;
      })
    );
  }
}

 function paymentMethodCardUHU(
  textTitle: string,
  paymentDetails: PaymentDetails
): Card {
  return {
    title: textTitle,
    text: [
      paymentDetails.cardType?.name,
      paymentDetails.accountHolderName,
      paymentDetails.cardNumber,
    ],
  } as Card;
}

 